<script lang="ts">
  import { t } from '$core/i18n';
  import {
    circadianDate,
    validateSleep,
    clampQuality,
    type SleepInput,
    type SleepValidation
  } from '$features/sleep';
  import {
    listSleepRecords,
    upsertSleepRecord,
    deleteSleepRecord
  } from '$db/local/repositories';
  import type { SleepRecord } from '$domain/types';
  import { Button } from '$ui/primitives/Button';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let records = $state<SleepRecord[]>([]);
  let bedtime = $state('');
  let wakeTime = $state('');
  let quality = $state(3);
  let saving = $state(false);

  let validation: SleepValidation = $derived(
    bedtime && wakeTime
      ? validateSleep({ bedtime, wakeTime, quality } as SleepInput)
      : { ok: false, error: null, durationMinutes: 0, circadianDate: '' }
  );

  let editingDate: string | null = $derived(
    bedtime ? circadianDate(bedtime) : null
  );

  let existingForDate: SleepRecord | null = $derived(
    editingDate ? records.find((r) => r.date === editingDate) ?? null : null
  );

  async function refreshRecords() {
    records = await listSleepRecords();
  }

  $effect(() => {
    void refreshRecords();
  });

  function toLocalInput(iso: string): string {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function fromLocalInput(value: string): string {
    return new Date(value).toISOString();
  }

  function prefillFromExisting(record: SleepRecord) {
    bedtime = toLocalInput(record.bedtime);
    wakeTime = toLocalInput(record.wakeTime);
    quality = record.quality;
  }

  function clearForm() {
    bedtime = '';
    wakeTime = '';
    quality = 3;
  }

  async function submit() {
    if (!bedtime || !wakeTime) return;
    const bedIso = fromLocalInput(bedtime);
    const wakeIso = fromLocalInput(wakeTime);
    const v = validateSleep({ bedtime: bedIso, wakeTime: wakeIso, quality });
    if (!v.ok) return;
    saving = true;
    try {
      await upsertSleepRecord({
        date: v.circadianDate,
        bedtime: bedIso,
        wakeTime: wakeIso,
        durationMinutes: v.durationMinutes,
        quality: clampQuality(quality)
      });
      await refreshRecords();
      clearForm();
    } finally {
      saving = false;
    }
  }

  async function removeRecord(record: SleepRecord) {
    await deleteSleepRecord(record.id);
    await refreshRecords();
    clearForm();
  }

  function fmtDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return t('sleep.durationLabel', { h, m });
  }

  function fmtDate(date: string): string {
    const [y, mo, d] = date.split('-').map(Number);
    const dt = new Date(y!, (mo ?? 1) - 1, d ?? 1);
    return dt.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  function fmt12h(iso: string): string {
    if (!iso) return '-';
    const d = new Date(iso);
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  function stars(quality: number): string {
    const filled = Math.max(0, Math.min(5, Math.round(quality)));
    return '★'.repeat(filled) + '☆'.repeat(5 - filled);
  }

  let errorMessage = $derived(
    validation.error === 'invalid_order'
      ? t('sleep.errorInvalidOrder')
      : validation.error === 'too_short'
        ? t('sleep.errorTooShort')
        : validation.error === 'too_long'
          ? t('sleep.errorTooLong')
          : ''
  );
</script>

<section class="view">
  <header class="view-head">
    <h1 class="view-title">{t('sleep.title')}</h1>
  </header>

  <div class="sleep-grid">
    <!-- Log sleep -->
    <section class="sleep-log">
      <h3>Log sleep</h3>
      <div class="grid-2">
        <div class="field-block">
          <label class="field-label" for="sleep-bedtime">{t('sleep.bedtime')}</label>
          <input
            id="sleep-bedtime"
            type="datetime-local"
            bind:value={bedtime}
            class="native-input"
          />
        </div>
        <div class="field-block">
          <label class="field-label" for="sleep-wake">{t('sleep.wakeTime')}</label>
          <input
            id="sleep-wake"
            type="datetime-local"
            bind:value={wakeTime}
            class="native-input"
          />
        </div>
        <div class="field-block">
          <label class="field-label" for="sleep-quality">
            {t('sleep.quality')} · {quality}/5
          </label>
          <input
            id="sleep-quality"
            type="range"
            min="1"
            max="5"
            step="1"
            bind:value={quality}
            aria-label={t('sleep.quality')}
            aria-valuetext={`${quality} / 5`}
          />
          <div class="quality-hint">
            <span>{t('sleep.qualityLow')}</span>
            <span>{t('sleep.qualityHigh')}</span>
          </div>
        </div>
        <div class="field-block">
          <span class="field-label">{t('sleep.duration')}</span>
          <span class="calculated-hours">
            {validation.ok ? fmtDuration(validation.durationMinutes) : '—'}
          </span>
        </div>
      </div>

      {#if errorMessage}
        <div class="error" role="alert">{errorMessage}</div>
      {/if}

      <p class="hint">{t('sleep.circadianHint')}</p>

      <div class="actions">
        <Button
          variant="filled"
          disabled={!validation.ok || saving}
          loading={saving}
          onclick={() => void submit()}
        >
          {saving
            ? t('sleep.saving')
            : existingForDate
              ? t('sleep.update')
              : t('sleep.save')}
        </Button>
        {#if bedtime || wakeTime}
          <Button variant="plain" onclick={clearForm}>{t('sleep.cancel')}</Button>
        {/if}
        {#if existingForDate}
          <Button
            variant="danger"
            onclick={() => existingForDate && void removeRecord(existingForDate)}
          >
            {t('sleep.delete')}
          </Button>
        {/if}
      </div>
    </section>

    <!-- Full sleep history -->
    <section class="sleep-history">
      <h3>Full sleep history</h3>
      {#if records.length === 0}
        <EmptyState
          icon="moon"
          title={t('sleep.noRecords')}
          description={t('sleep.noRecordsHint')}
        />
      {:else}
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Hours</th>
                <th>Quality</th>
                <th>Bedtime</th>
                <th>Wake up</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each records.slice(0, 20) as r (r.id)}
                <tr class="editable-record" onclick={() => prefillFromExisting(r)}>
                  <td>{fmtDate(r.date)}</td>
                  <td class="mono">{fmtDuration(r.durationMinutes)}</td>
                  <td>
                    <span class="stars" aria-label={`${r.quality} de 5`}>
                      {stars(r.quality)}
                    </span>
                    <span class="quality-num">{r.quality}/5</span>
                  </td>
                  <td class="mono">{fmt12h(r.bedtime)}</td>
                  <td class="mono">{fmt12h(r.wakeTime)}</td>
                  <td>
                    <button
                      type="button"
                      class="icon-btn-delete"
                      aria-label="Eliminar"
                      onclick={(e) => {
                        e.stopPropagation();
                        void removeRecord(r);
                      }}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
  </div>
</section>

<style>
  /* El grueso del look viene de legacy.css (tabla genérica, .card,
     .field-block, .native-input). Aquí solo lo específico. */

  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    width: 100%;
  }

  .view-head {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .view-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: var(--color-text-primary);
  }

  .sleep-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-6);
    align-items: start;
  }

  @media (max-width: 1024px) {
    .sleep-grid {
      grid-template-columns: 1fr;
    }
  }

  .sleep-log,
  .sleep-history {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .sleep-log h3,
  .sleep-history h3 {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.1rem;
    letter-spacing: -0.01em;
    color: var(--color-text-primary);
  }

  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-4) var(--space-5);
  }

  @media (max-width: 640px) {
    .grid-2 {
      grid-template-columns: 1fr;
    }
  }

  .field-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }

  .field-label {
    font-size: 0.7rem;
    font-weight: var(--weight-semibold);
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .native-input {
    width: 100%;
    height: 40px;
    padding: 0 var(--space-3);
    font-family: var(--font-body, var(--font-sans));
    font-size: var(--text-body);
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    transition: border-color var(--dur-fast) var(--ease-out);
  }

  .native-input:focus-visible {
    border-color: var(--color-accent);
    outline: 2px solid var(--color-accent-tint);
    outline-offset: 0;
  }

  input[type='range'] {
    width: 100%;
    accent-color: var(--color-accent);
  }

  .quality-hint {
    display: flex;
    justify-content: space-between;
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .calculated-hours {
    font-family: var(--font-mono);
    font-size: 1.2rem;
    font-weight: 700;
    color: var(--color-accent);
    line-height: 40px;
  }

  .error {
    padding: var(--space-3);
    font-size: var(--text-sm);
    color: var(--color-danger);
    background: var(--color-danger-tint);
    border-left: 3px solid var(--color-danger);
    border-radius: var(--radius-sm);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
  }

  .editable-record {
    cursor: pointer;
    transition: background var(--duration-instant) var(--ease-out);
  }

  .editable-record:hover {
    background: var(--color-surface-hover);
  }

  .stars {
    color: var(--color-accent);
    letter-spacing: 1px;
  }

  .quality-num {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    margin-left: 6px;
    color: var(--color-text-secondary);
  }

  .icon-btn-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: transparent;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
    font-size: 0.9rem;
    transition: border-color var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
  }

  .icon-btn-delete:hover {
    border-color: var(--color-danger);
    color: var(--color-danger);
  }
</style>